import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminContext = createContext();

export const AdminProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [username, setUsername] = useState(null);

  useEffect(() => {
    const savedToken = localStorage.getItem('brusa-admin-token');
    if (savedToken) {
      verifyToken(savedToken);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (tokenToVerify) => {
    try {
      const res = await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${tokenToVerify}` }
      });
      setToken(tokenToVerify);
      setIsAuthenticated(true);
      setUserRole(res.data.role || 'admin');
      setUsername(res.data.username || 'admin');
    } catch (error) {
      localStorage.removeItem('brusa-admin-token');
      setToken(null);
      setIsAuthenticated(false);
      setUserRole(null);
      setUsername(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (usernameInput, password) => {
    try {
      const response = await axios.post(`${API}/admin/login`, { username: usernameInput, password });
      const newToken = response.data.token;
      localStorage.setItem('brusa-admin-token', newToken);
      setToken(newToken);
      setIsAuthenticated(true);
      setUserRole(response.data.role || 'admin');
      setUsername(usernameInput);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await axios.post(`${API}/admin/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      // Ignore logout errors
    }
    localStorage.removeItem('brusa-admin-token');
    setToken(null);
    setIsAuthenticated(false);
    setUserRole(null);
    setUsername(null);
  };

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${token}`
  });

  return (
    <AdminContext.Provider value={{ 
      isAuthenticated, 
      loading, 
      login, 
      logout, 
      token,
      userRole,
      username,
      getAuthHeaders 
    }}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};
